/**
 * TenantUserAccessModal Component
 * Modal wrapper for TenantUserAccessList component.
 *
 * Displays a dialog containing the list of users with access to a tenant,
 * with the ability to grant access to additional users.
 */

import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  makeStyles,
} from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { TenantUserAccessList } from './TenantUserAccessList';

/**
 * Minimal tenant info required for the modal.
 * Works with both TenantListItem and OrganizationTenant.
 */
export interface TenantUserAccessModalTenant {
  tenant_id: string;
  tenant_name: string;
}

export interface TenantUserAccessModalProps {
  isOpen: boolean;
  tenant: TenantUserAccessModalTenant;
  onClose: () => void;
}

const useStyles = makeStyles({
  dialogSurface: {
    minWidth: '800px',
    maxWidth: '1000px',
    maxHeight: '80vh',
  },
  dialogContent: {
    minHeight: '400px',
  },
});

export const TenantUserAccessModal: React.FC<TenantUserAccessModalProps> = ({
  isOpen,
  tenant,
  onClose,
}) => {
  const styles = useStyles();

  return (
    <Dialog open={isOpen}>
      <DialogSurface
        className={styles.dialogSurface}
        data-testid="tenant-user-access-modal"
      >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<Dismiss20Regular />}
                onClick={onClose}
                aria-label="Close"
                data-testid="user-access-modal-close-button"
              />
            }
          >
            User Access - {tenant.tenant_name}
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            <TenantUserAccessList tenantId={tenant.tenant_id} />
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={onClose}
            >
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
